import React, { useState, useEffect, useRef, useCallback } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { useIntl } from 'react-intl';
import loading from '../loading.svg';
import loadingWhite from '../loading_white.svg';

export default function UserSelector(props) {
    const {
        users,
        handleSwitchUser,
        usersLength,
        handleFilterChange,
        clientId,
        username
    } = props;

    const intl = useIntl();
    const [showUserSelector, setShowUserSelector] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [searchValue, setSearchValue] = useState('');
    const searchRef = useRef(null);
    const listRef = useRef(null);
    const itemRefs = useRef([]);
    const [switchingUser, setSwitchingUser] = useState(false);

    // Debounced search
    useEffect(() => {
        const timeout = setTimeout(() => {
            handleFilterChange({ target: { value: searchValue } });
        }, 300);
        return () => clearTimeout(timeout);
    }, [searchValue]);

    useEffect(() => {
        if (showUserSelector && searchRef.current) {
            searchRef.current.focus();
        }
    }, [showUserSelector]);

    // Scroll to focused item
    useEffect(() => {
        if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
            itemRefs.current[focusedIndex].scrollIntoView({ block: 'nearest' });
        }
    }, [focusedIndex]);

    const handleKeyDown = useCallback((e) => {
        if (!showUserSelector) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex((prev) => (prev + 1) % users.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex((prev) => (prev - 1 + users.length) % users.length);
        } else if (e.key === 'Enter' && focusedIndex >= 0) {
            e.preventDefault();
            handleSwitchUser(users[focusedIndex].id);
            setShowUserSelector(false);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setShowUserSelector(false);
        }
    }, [focusedIndex, users, showUserSelector]);

    const handleInputChange = (e) => {
        setSearchValue(e.target.value);
        setFocusedIndex(-1);
    };

    return (
        <ClickAwayListener onClickAway={() => setShowUserSelector(false)}>
            <div className='relative text-gray-600'>
                <div
                    title='Select User'
                    onClick={() => {
                        setShowUserSelector(prev => !prev);
                        setFocusedIndex(-1);
                    }}
                    onKeyDown={handleKeyDown}
                    tabIndex={0}
                    aria-haspopup="listbox"
                    aria-expanded={showUserSelector}
                    className="m-4 flex h-8 w-8 items-center justify-center rounded-full bg-green-200 font-bold text-green-600 ring-2 ring-white cursor-pointer uppercase"
                >
                    {switchingUser ? (
                        <img style={{ width: '15px' }} className='text-slate-500' src={loadingWhite} alt='loading' />
                    ) : (
                        username?.[0] || <i className='material-icons'>person</i>
                    )}
                </div>

                {showUserSelector && (
                    <div
                        className="p-2 w-[280px] absolute top-[50px] right-0 rtl:left-0 rtl:right-auto rounded-md shadow-lg bg-white border border-gray-300"
                        onKeyDown={handleKeyDown}
                        role="listbox"
                        aria-label={intl.formatMessage({ id: 'app.userSelector.listAria', defaultMessage: 'User list' })}
                        ref={listRef}
                    >
                        {usersLength > 5 && (
                            <div className='w-full p-2'>
                                <input
                                    ref={searchRef}
                                    className="w-full bg-white border-0 border-b-[1px] border-b-green-500 focus:outline-none"
                                    style={{ textAlign: 'start' }}
                                    type="text"
                                    value={searchValue}
                                    onChange={handleInputChange}
                                    placeholder={intl.formatMessage({ id: 'app.userSelector.searchPlaceholder', defaultMessage: 'Search' })}
                                    aria-label={intl.formatMessage({ id: 'app.userSelector.searchAria', defaultMessage: 'Search users' })}
                                />
                            </div>
                        )}

                        <ul className="max-h-[250px] overflow-y-auto mt-1 divide-y divide-gray-100">
                            {users.length > 0 ? (
                                users.map((item, index) => (
                                    <li
                                        key={item.id}
                                        ref={el => itemRefs.current[index] = el}
                                        onClick={() => {
                                            handleSwitchUser(item.id);
                                            setSwitchingUser(true);
                                            setShowUserSelector(false);
                                        }}
                                        role="option"
                                        aria-selected={index === focusedIndex}
                                        className={`flex justify-between gap-x-6 py-2 cursor-pointer rounded-md p-2 hover:bg-gray-100 ${index === focusedIndex ? 'bg-gray-100' : ''
                                            }`}
                                    >
                                        <div className="flex min-w-0 gap-x-4">
                                            <div className="min-w-0 flex-auto">
                                                <div className="whitespace-nowrap flex flex-row flex-wrap items-center gap-1" title={item.name || item.username}>
                                                    <div className='text-ellipsis max-w-[170px] overflow-hidden text-sm font-semibold leading-6 text-gray-900'>
                                                        {item.name || item.username}
                                                    </div>

                                                    {item.id === clientId && (
                                                        <span className="inline-flex shrink-0 items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                                                            {intl.formatMessage({ id: 'app.userSelector.signedIn', defaultMessage: 'Signed in' })}
                                                        </span>
                                                    )}
                                                </div>
                                                <p
                                                    className="whitespace-nowrap text-ellipsis w-[150px] overflow-hidden mt-1 truncate text-xs leading-5 text-gray-500"
                                                    title={`@${item?.username}`}
                                                >
                                                    @{item?.username}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="shrink-0 sm:flex sm:flex-col sm:items-end">
                                            <p className="whitespace-nowrap text-ellipsis w-[80px] overflow-hidden text-sm leading-6 text-gray-900 inline-block h-[21px]" style={{ textAlign: 'end' }} title={item.country}>
                                                {item.country}
                                            </p>
                                            <p className="whitespace-nowrap text-ellipsis w-[80px] overflow-hidden mt-1 text-xs leading-5 text-gray-500 h-[21px]" style={{ textAlign: 'end' }} title={item.crop}>
                                                {item.crop}
                                            </p>
                                        </div>
                                    </li>
                                ))
                            ) : (
                                <div className='flex justify-center'>
                                    {intl.formatMessage({ id: 'app.userSelector.notFound', defaultMessage: 'User not found!' })}
                                </div>
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </ClickAwayListener>
    );
}
